import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react"
import { useQuery, UseQueryResult } from "react-query"
import { AxiosError } from "axios"
import { ndfDepensesListingRequest } from "../../../../core/_request"
import MyModal from "../../../../../../../utils/MyModal"
import { NdfAddDepenseGrouped } from "../../depense/add/grouped/NdfAddDepenseGrouped"
import MyCard from "../../../../../../../utils/MyCard"
import { Listing } from "./Listing"
import { NdfUpdateDetailsWrapperFormAddDepenseSimple } from "../../depense/add/simple/NdfAddDepenseSimple"
import NdfEditDepenseSimple from "../../depense/edit/simple/NdfEditDepenseSimple"
import NdfEditDepenseGrouped from "../../depense/edit/grouped/NdfEditDepenseGrouped"
import "../styles.scss"
import NoticeUtilisation from "../NoticeUtilisation"
import { IConsultantNdfDetailModel } from "@common-models/*"

const NdfDetailsSalarie = ({ ndfId, editionMode = true }: IPropsModel) => {
     const [totalDepenses, setTotalDepenses] = useState<number>(0)

     const getNdfDetailsQuery = useQuery<IConsultantNdfDetailModel[], AxiosError>(["ndfDetails", ndfId], () => ndfDepensesListingRequest(ndfId).then(r => r.data))

     useEffect(() => {
          computeTotalExpenses(getNdfDetailsQuery.data || [])
     }, [getNdfDetailsQuery.data])

     function computeTotalExpenses(items: IConsultantNdfDetailModel[]) {
          let total = 0
          items.map(item => {
               if (item.isParent && item.enfants) {
                    item.enfants.map(enfant => {
                         total += enfant.montantTtc as number
                    })
               } else {
                    total += item.montantTtc as number
               }
          })
          setTotalDepenses(total)
     }

     // States
     const [isAddDetailsModalShown, setIsAddDetailsModalShown] = useState<boolean>(false)
     const [currentDepenseInEdition, setCurrentDepenseInEdition] = useState<IConsultantNdfDetailModel>()
     const [typeAddDetail, setTypeAddDetail] = useState<"simple" | "group" | null>(null)
     const [isCardDetailSimpleCollapsed, setIsCardDetailSimpleCollapsed] = useState<boolean>(false)
     const [isNoticeUtilisationShownInModal, setIsNoticeUtilisationShownInModal] = useState<boolean>(false)

     // Modal add depense
     const FormAddDetailsInModal = useMemo(() => {
          return (
               <MyModal
                    title={<span>Ajouter une dépense</span>}
                    show={isAddDetailsModalShown}
                    handleClose={() => {
                         setIsAddDetailsModalShown(false)
                         setTypeAddDetail(null)
                    }}
                    fullscreen
               >
                    {typeAddDetail === null ? (
                         <div className="animate__animated animate__fadeIn py-10">
                              <div className="text-center mb-10">
                                   <h2 className="fw-boldest text-gray-900 mb-2">Que souhaitez-vous ajouter ?</h2>
                                   <p className="text-muted fw-semibold fs-5">Choisissez le type de saisie qui correspond à votre note de frais.</p>
                              </div>

                              <div className="row g-6 justify-content-center">
                                   {/* Option Dépense Simple */}
                                   <div className="col-md-5">
                                        <div
                                             className="card border border-1 border-dashed border-primary bg-hover-light-primary cursor-pointer transition-all h-100"
                                             onClick={() => setTypeAddDetail("simple")}
                                        >
                                             <div className="card-body d-flex flex-column align-items-center text-center p-10">
                                                  <div className="symbol symbol-70px mb-5">
                                                       <div className="symbol-label bg-light-primary">
                                                            <i className="fas fa-file-invoice-dollar fs-2x text-primary"></i>
                                                       </div>
                                                  </div>
                                                  <h3 className="fw-boldest mb-2">Dépense Simple</h3>
                                                  <p className="text-gray-600 fs-6">
                                                       Saisie rapide pour un <strong>seul justificatif</strong> ou une facture ponctuelle.
                                                  </p>
                                                  <span className="badge badge-light-primary fw-bold">Un seul justificatif</span>
                                             </div>
                                        </div>
                                   </div>

                                   {/* Option Saisie Groupée */}
                                   <div className="col-md-5">
                                        <div
                                             className="card border border-1 border-dashed border-info bg-hover-light-info cursor-pointer transition-all h-100"
                                             onClick={() => setTypeAddDetail("group")}
                                        >
                                             <div className="card-body d-flex flex-column align-items-center text-center p-10">
                                                  <div className="symbol symbol-70px mb-5">
                                                       <div className="symbol-label bg-light-info">
                                                            <i className="fas fa-layer-group fs-2x text-info"></i>
                                                       </div>
                                                  </div>
                                                  <h3 className="fw-boldest mb-2">Saisie Groupée</h3>
                                                  <p className="text-gray-600 fs-6">
                                                       Agrégez <strong>plusieurs lignes</strong> de frais sous une seul et même note.
                                                  </p>
                                                  <span className="badge badge-light-info fw-bold">Multiples lignes</span>
                                             </div>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    ) : (
                         <div className="animate__animated animate__fadeIn">
                              <div className="d-flex align-items-center mb-5 gap-3">
                                   <button className="btn btn-sm btn-icon btn-light" onClick={() => setTypeAddDetail(null)}>
                                        <i className="bi bi-arrow-left fs-3"></i>
                                   </button>
                                   <h3 className="fw-boldest mb-0">{typeAddDetail === "simple" ? "Dépense Simple" : "Dépenses Groupées"}</h3>
                              </div>
                              <MyCard>
                                   <MyCard.Body>
                                        {typeAddDetail === "group" && <NdfAddDepenseGrouped />}
                                        {typeAddDetail === "simple" && <NdfUpdateDetailsWrapperFormAddDepenseSimple />}
                                   </MyCard.Body>
                              </MyCard>
                         </div>
                    )}
               </MyModal>
          )
     }, [isAddDetailsModalShown, typeAddDetail])

     function closeAndReopenAddDetailModal() {
          setIsAddDetailsModalShown(false)
          setIsAddDetailsModalShown(true)
     }

     return (
          <>
               <NdfDetailsSalarieContext.Provider
                    value={{
                         setIsAddDetailsModalShown: setIsAddDetailsModalShown,
                         ndfId,
                         editionMode,
                         getNdfDetailsQuery,
                         closeAndReopenAddDetailModal,
                         setCurrentDepenseInEdition,
                         totalDepenses,
                         computeTotalExpenses,
                         setTypeAddDetail,
                         setIsCardDetailSimpleCollapsed,
                    }}
               >
                    {/* EDITION MODE ONLY: didacticiel + recharger + ajouter*/}
                    {editionMode && (
                         <>
                              <div className="d-flex justify-content-between mb-7">
                                   <div className="w-xxl-15 w-xl-20 w-lg-25 w-md-30 w-sm-35 w-60">
                                        <button className={`btn btn-primary btn-sm d-flex `} onClick={() => setIsNoticeUtilisationShownInModal(true)}>
                                             <i className={"fas fa-file-alt fs-3 p-0 me-2"} />
                                             Notice d'utilisation
                                        </button>
                                   </div>
                                   <div>
                                        <button className={`btn btn-light-primary btn-sm me-2`} onClick={() => getNdfDetailsQuery.refetch()}>
                                             <i className={"fas fa-sync me-md-2 pe-0 fs-3"} />
                                             <span className={`d-md-inline d-none`}>Recharger la liste</span>
                                        </button>
                                        <button className={`btn btn-primary btn-sm`} onClick={() => setIsAddDetailsModalShown(true)}>
                                             <i className={"fas fa-plus-circle me-md-2 pe-0 fs-3"} />
                                             <span className={`d-md-inline d-none`}>Ajouter une dépense</span>
                                        </button>
                                   </div>
                              </div>
                              <div className="separator separator-dashed mb-4 mt-4" />
                         </>
                    )}

                    <Listing />

                    {isAddDetailsModalShown && FormAddDetailsInModal}

                    {currentDepenseInEdition && (
                         <MyModal title={<span>Edition de la dépense</span>} show={true} handleClose={() => setCurrentDepenseInEdition(undefined)} size={"xl"}>
                              <>
                                   {currentDepenseInEdition.isParent ? (
                                        <NdfEditDepenseGrouped detail={currentDepenseInEdition} />
                                   ) : (
                                        <NdfEditDepenseSimple detail={currentDepenseInEdition} />
                                   )}
                              </>
                         </MyModal>
                    )}

                    {isNoticeUtilisationShownInModal && (
                         <MyModal title={<>Notice d'utilisation</>} show={true} handleClose={() => setIsNoticeUtilisationShownInModal(false)} size={"lg"} noPadding>
                              <NoticeUtilisation finaliserCallback={() => setIsNoticeUtilisationShownInModal(false)} />
                         </MyModal>
                    )}
               </NdfDetailsSalarieContext.Provider>
          </>
     )
}

const NdfDetailsSalarieContext = createContext<IContextPropsModel>({} as IContextPropsModel)

const useNdfDetailsSalarieContext = () => useContext(NdfDetailsSalarieContext)

interface IPropsModel {
     ndfId: number
     editionMode?: boolean
}

interface IContextPropsModel {
     closeAndReopenAddDetailModal: () => void
     setIsAddDetailsModalShown: Dispatch<SetStateAction<boolean>>
     setCurrentDepenseInEdition: Dispatch<SetStateAction<IConsultantNdfDetailModel | undefined>>
     ndfId: number
     editionMode: boolean
     getNdfDetailsQuery: UseQueryResult<IConsultantNdfDetailModel[], AxiosError>
     totalDepenses: number
     computeTotalExpenses: (items: IConsultantNdfDetailModel[]) => void
     setTypeAddDetail: Dispatch<SetStateAction<"simple" | "group" | null>>
     setIsCardDetailSimpleCollapsed: React.Dispatch<React.SetStateAction<boolean>>
}

export { NdfDetailsSalarie, useNdfDetailsSalarieContext }
